import {
  Col,
  Input,
  Row,
  Skeleton,
} from "antd";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { FaSearch, FaEye, FaTrash, FaGripVertical } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Get } from "../../config/api/get";
import { Delete } from "../../config/api/delete";
import { Post } from "../../config/api";
import { PLAN } from "../../config/constants/api";
import { useDebouncedCallback } from "use-debounce";
import Swal from "sweetalert2";

const MembershipPackages = () => {
  const token = useSelector((state) => state.user.userToken);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Ref to store latest keyword for debounced callback
  const keywordRef = useRef(keyword);
  
  // Update ref whenever keyword changes
  useEffect(() => {
    keywordRef.current = keyword;
  }, [keyword]);
  
  // Fetch plans
  const getPlans = () => {
    setLoading(true);
    // This is a public endpoint, so we can pass null for token or still use token
    Get(PLAN.getPlans, token, {})
      .then((response) => {
        // Handle both response structures: {success: true, data: [...]} or just data array
        let plansData = [];
        if (response?.success && response?.data) {
          plansData = response.data;
        } else if (Array.isArray(response?.data)) {
          plansData = response.data;
        } else if (Array.isArray(response)) {
          plansData = response;
        }
        
        // Filter plans based on keyword if provided
        let filteredPlans = plansData;
        if (keywordRef.current) {
          const searchTerm = keywordRef.current.toLowerCase();
          filteredPlans = plansData.filter(
            (plan) =>
              plan.title?.toLowerCase().includes(searchTerm) ||
              plan.hoursType?.toLowerCase().includes(searchTerm)
          );
        }
        // Sort: default packages first, then by order, then by creation date
        filteredPlans.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          // Sort by order field if available
          if (a.order !== undefined && b.order !== undefined) {
            if (a.order !== b.order) return a.order - b.order;
          }
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setPlans(filteredPlans);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Error Fetching Plans", err);
        setLoading(false);
        // Set empty array on error to prevent infinite loading
        setPlans([]);
      });
  };

  useEffect(() => {
    getPlans();
    // eslint-disable-next-line
  }, []);

  // Debounced function for search
  const debouncedSearch = useDebouncedCallback((searchValue) => {
    getPlans();
  }, 500);

  const handleSearch = (value) => {
    setKeyword(value);
    debouncedSearch(value);
  };

  const handleView = (planId) => {
    navigate(`/membershipPackages/view/${planId}`);
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target);
    e.target.style.opacity = "0.5";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Optimistically update the UI
    const newPlans = [...plans];
    const draggedPlan = newPlans[draggedIndex];
    newPlans.splice(draggedIndex, 1);
    newPlans.splice(dropIndex, 0, draggedPlan);
    setPlans(newPlans);
    setDraggedIndex(null);

    // Send new order to backend
    const planIds = newPlans.map(plan => plan._id);
    try {
      const response = await Post(PLAN.reorderPlansByDrag, { planIds }, token);
      if (response?.success || response?.status) {
        // Refresh to get updated order values
        getPlans();
        Swal.fire({
          title: "Success!",
          text: response?.message || "Plans reordered successfully",
          icon: "success",
          confirmButtonColor: "#3085d6",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        // Revert on error
        getPlans();
        Swal.fire({
          title: "Error!",
          text: response?.message || "Failed to reorder plans",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    } catch (err) {
      // Revert on error
      getPlans();
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reorder plans. Please try again.";
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDelete = (planId, planTitle) => {
    Swal.fire({
      title: "Are you sure?",
      html: `
        <p>You are about to delete the plan:</p>
        <p><strong>${planTitle}</strong></p>
        <p>This action cannot be undone.</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusConfirm: false,
      focusCancel: true,
    }).then((result) => {
      if (result.isConfirmed) {
        // Show loading state
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while we delete the plan.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        Delete(PLAN.deletePlan + planId, token, null)
          .then((response) => {
            if (response?.success || response?.status) {
              // Refresh the plans list
              getPlans();
              
              Swal.fire({
                title: "Deleted!",
                text: response?.message || "Plan deleted successfully.",
                icon: "success",
                confirmButtonColor: "#3085d6",
              });
            } else {
              Swal.fire({
                title: "Error!",
                text: response?.message || "Failed to delete plan.",
                icon: "error",
                confirmButtonColor: "#d33",
              });
            }
          })
          .catch((err) => {
            const errorMessage =
              err?.response?.data?.message ||
              err?.message ||
              "Failed to delete plan. Please try again.";
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
              confirmButtonColor: "#d33",
            });
          });
      }
    });
  };


  return (
    <Row justify="center">
      <Col md={23} xs={22}>
        <div
          className="bg-white rounded-lg sm:py-16 py-8 my-12"
          style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
        >
          <div className="flex justify-between items-center mx-4 lg:mx-8">
            <div className="flex justify-center items-center">
              <h3 className="font-tavi lg:text-3xl text-2xl max-sm:text-lg font-normal text-black">
                Membership Packages
              </h3>
            </div>
            <div className="flex justify-center items-center gap-2">
              <Input
                size="large"
                className="mainInput dashInput w-[500px] max-md:w-full max-md:max-w-full"
                placeholder="Search by title or type"
                value={keyword}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ fontSize: "14px" }}
                suffix={
                  <FaSearch
                    style={{
                      color: "#818181",
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  />
                }
              />
            </div>
          </div>

          <Row style={{ overflow: "auto" }} className="px-6 mt-8">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Skeleton active />
                <br />
              </div>
            ) : plans && plans.length > 0 ? (
              <div className="w-full overflow-x-auto">
                {/* Table Header */}
                <div className="min-w-full flex flex-row     gap-0 py-3 px-4 bg-gray-100 rounded-t-lg border-b-2 border-gray-200 font-euclid text-xs font-bold text-black">
                  <div className="text-center flex-1/2">S.NO</div> 
                  <div className="text-center flex-1">Title</div>
                  <div className="text-center flex-1">Type</div>
                  <div className="text-center flex-1">Hours</div>
                  <div className="text-center flex-1">Duration</div>
                  <div className="text-center flex-1">Discount</div>
                  <div className="text-center flex-1">Actual</div>
                  <div className="text-center flex-1">Discounted</div>
                  <div className="text-center flex-1">Actions</div>
                </div>
                
                {/* Draggable Rows */}
                <div className="bg-white rounded-b-lg">
                  {plans.map((plan, index) => (
                    <div
                      key={plan._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`min-w-full flex flex-row gap-0 py-4 px-4 border-b border-gray-200 items-center transition-all duration-200 ${
                        draggedIndex === index ? 'opacity-50 bg-gray-100' : ''
                      } ${
                        dragOverIndex === index ? 'bg-blue-50 border-blue-300 border-t-2' : ''
                      } hover:bg-gray-50 cursor-move`}
                    >
                      {/* S.NO */}
                      <div 
                          className="flex flex-1/2 items-center justify-center p-2 hover:bg-gray-200 rounded cursor-move"
                          onMouseDown={(e) => e.stopPropagation()}
                          title="Drag to reorder"
                        >
                          <FaGripVertical className="text-gray-500 text-lg" />
                        </div>
                      <div className="text-center flex-1/2 font-euclid text-xs text-[#575757]">
                        
                        {(index < 10 ? "0" : "") + (index + 1)}
                      </div>
                      
                      {/* Title */}
                      <div className="text-center flex-1 font-euclid text-xs text-[#575757]">
                        <div className="flex items-center justify-center gap-2">
                          <span>{plan.title}</span>
                          {plan.isPopular && (
                            <span className="text-[10px] text-[#CD810D] font-bold">POPULAR</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Type */}
                      <div className="text-center flex-1 font-euclid text-xs text-[#575757]">
                        {plan.hoursType}
                      </div>
                      
                      {/* Hours */}
                      <div className="text-center flex-1 font-euclid text-xs text-[#575757]">
                        {plan.hours || 0}
                      </div>
                      
                      {/* Booking Duration */}
                      <div className="text-center flex-1 font-euclid text-xs text-[#575757]">
                        {plan.bookingDuration} hrs
                      </div>
                      
                      {/* Discount */}
                      <div className="text-center flex-1 font-euclid text-xs text-[#575757]">
                        {plan.savePercent}%
                      </div>
                      
                      {/* Actual Amount */}
                      <div className="text-center flex-1 font-euclid text-xs text-[#575757]">
                        ${plan.actualAmount?.toLocaleString()}
                      </div>
                      
                      {/* Discounted Amount */}
                      <div className="text-center flex-1 font-euclid text-xs text-[#575757]">
                        ${plan.discountedAmount?.toLocaleString()}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex justify-center items-center gap-3 flex-1">
                    
                        <div 
                          className="flex justify-center items-center bg-[#9CFEC4] rounded-[100%] w-[36px] h-[36px] hover:bg-[#7FE5B0] transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(plan._id);
                          }}
                          title="View"
                        >
                          <FaEye
                            className="text-[#089B44] cursor-pointer h-[16px] w-[16px]"
                          />
                        </div>
                        <div 
                          className="flex justify-center items-center bg-[#FFE5E5] rounded-[100%] w-[36px] h-[36px] hover:bg-[#FFCCCC] transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(plan._id, plan.title);
                          }}
                          title="Delete"
                        >
                          <FaTrash
                            className="text-[#DC3545] cursor-pointer h-[16px] w-[16px]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="w-full text-center py-8">
                <p className="font-euclid text-gray-500">No plans found</p>
              </div>
            )}
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default MembershipPackages;

